import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import GameEngine from './GameEngine';
import { HalfTimeScreen } from '../../components/ArcadeGame/HalfTimeScreen';
import { GameOverScreen } from '../../components/ArcadeGame/GameOverScreen';
import './ArcadeGame.css';

const HALF_DURATION = 120; // 2 minutes per half

const COMMENTARY = {
    playerGoal: ['¡GOLAZO!', '¡INCREÍBLE!', 'INTO THE NET!', '¡BRUTAL!', 'WHAT A SHOT!'],
    aiGoal:     ['Oh no...', 'They scored!', 'Stay focused!', 'CONCEDED!', 'Keep it up!'],
    aiSave:     ['GREAT SAVE!', 'WALL OF STEEL!', 'NO WAY!', 'Incredible reflex!'],
    playerSave: ['What a keeper!', 'SAVED!', 'Not today!', 'OUT!'],
    ahead2:     ["They're falling apart!", 'Dominating!', 'Keep the pressure!', 'More goals!'],
    behind2:    ['Stay focused!', "Don't give up!", 'We need a goal!', 'Push forward!'],
};

function randomPick(arr) {
    return arr[Math.floor(Math.random() * arr.length)];
}

function formatTime(seconds) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
}


export function ArcadeGame() {
    const containerRef  = useRef(null);
    const engineRef     = useRef(null);
    const history       = useHistory();
    const location      = useLocation();
    const playerRoster  = location.state?.playerRoster ?? null;
    const aiRoster      = location.state?.aiRoster     ?? null;

    const [gamePhase, setGamePhase]   = useState('playing'); // 'playing' | 'halftime' | 'gameover'
    const [half, setHalf]             = useState(1);
    const [timeLeft, setTimeLeft]     = useState(HALF_DURATION);
    const [score, setScore]           = useState({ player: 0, ai: 0 });
    const [inShootZone, setInShootZone] = useState(false);
    const [activePlayerInfo, setActivePlayerInfo] = useState(null);
    const [scorer, setScorer] = useState(null);
    const [scorerKey, setScorerKey] = useState(0);
    const [commentary, setCommentary] = useState('');
    const [commentaryKey, setCommentaryKey] = useState(0);
    const [countdown, setCountdown] = useState(null);
    const [isPaused, setIsPaused] = useState(false);
    const countdownTimeoutRef = useRef(null);

    const showCommentary = useCallback((text) => {
        setCommentary(text);
        setCommentaryKey(k => k + 1);
    }, []);

    const showCountdown = useCallback((n) => {
        if (countdownTimeoutRef.current) clearTimeout(countdownTimeoutRef.current);
        if (n === 0) {
            setCountdown('GO!');
            countdownTimeoutRef.current = setTimeout(() => setCountdown(null), 700);
        } else {
            setCountdown(String(n));
        }
    }, []);

    // Mount engine
    useEffect(() => {
        if (!containerRef.current || engineRef.current) return;

        engineRef.current = new GameEngine(containerRef.current, {
            roster: playerRoster,
            aiRoster,
            onGoal: ({ scorer: side, score: newScore, scorerName, scorerNumber }) => {
                setScore({ ...newScore });
                const diff = newScore.player - newScore.ai;
                let text;
                if (diff === 2)       text = randomPick(COMMENTARY.ahead2);
                else if (diff === -2) text = randomPick(COMMENTARY.behind2);
                else text = side === 'player' ? randomPick(COMMENTARY.playerGoal) : randomPick(COMMENTARY.aiGoal);
                showCommentary(text);
                if (scorerName) {
                    setScorer({ name: scorerName, number: scorerNumber });
                    setScorerKey(k => k + 1);
                }
            },
            onSave: ({ side }) => {
                showCommentary(side === 'ai'
                    ? randomPick(COMMENTARY.aiSave)
                    : randomPick(COMMENTARY.playerSave)
                );
            },
            onCountdown: showCountdown,
        });

        return () => {
            if (countdownTimeoutRef.current) clearTimeout(countdownTimeoutRef.current);
            if (engineRef.current) {
                engineRef.current.destroy();
                engineRef.current = null;
            }
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const togglePause = useCallback(() => {
        setIsPaused(p => {
            const next = !p;
            if (engineRef.current) next ? engineRef.current.pause() : engineRef.current.resume();
            return next;
        });
    }, []);

    // Game timer — only ticks while puck is live (no countdown, not paused)
    useEffect(() => {
        if (gamePhase !== 'playing' || countdown !== null || isPaused) return;

        const interval = setInterval(() => {
            setTimeLeft(t => {
                if (t <= 1) {
                    if (half === 1) {
                        setGamePhase('halftime');
                        if (engineRef.current) engineRef.current.pause();
                    } else {
                        setGamePhase('gameover');
                        if (engineRef.current) engineRef.current.pause();
                    }
                    return HALF_DURATION;
                }
                return t - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [gamePhase, half, countdown, isPaused]);

    // Shoot zone + active player polling
    useEffect(() => {
        const interval = setInterval(() => {
            if (engineRef.current) {
                setInShootZone(engineRef.current.isInShootZone());
                setActivePlayerInfo(engineRef.current.getActivePlayerInfo());
            }
        }, 100);
        return () => clearInterval(interval);
    }, []);

    const startSecondHalf = useCallback(() => {
        setIsPaused(false);
        setHalf(2);
        setTimeLeft(HALF_DURATION);
        setGamePhase('playing');
        if (engineRef.current) engineRef.current.resetForSecondHalf();
    }, []);

    const playAgain = useCallback(() => {
        if (engineRef.current) {
            engineRef.current.destroy();
            engineRef.current = null;
        }
        setScore({ player: 0, ai: 0 });
        setHalf(1);
        setTimeLeft(HALF_DURATION);
        setGamePhase('playing');
        setIsPaused(false);
        setCommentary('');
        if (containerRef.current) {
            engineRef.current = new GameEngine(containerRef.current, {
                roster: playerRoster,
                aiRoster,
                onGoal: ({ scorer: side, score: newScore, scorerName, scorerNumber }) => {
                    setScore({ ...newScore });
                    const diff = newScore.player - newScore.ai;
                    let text;
                    if (diff === 2)       text = randomPick(COMMENTARY.ahead2);
                    else if (diff === -2) text = randomPick(COMMENTARY.behind2);
                    else text = side === 'player' ? randomPick(COMMENTARY.playerGoal) : randomPick(COMMENTARY.aiGoal);
                    showCommentary(text);
                    if (scorerName) {
                        setScorer({ name: scorerName, number: scorerNumber });
                        setScorerKey(k => k + 1);
                    }
                },
                onSave: ({ side }) => {
                    showCommentary(side === 'ai'
                        ? randomPick(COMMENTARY.aiSave)
                        : randomPick(COMMENTARY.playerSave)
                    );
                },
                onCountdown: showCountdown,
            });
        }
    }, [playerRoster, aiRoster, showCommentary]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="arcade-overlay">

            {/* Back button */}
            <div className="arcade-back-btn">
                <span className="arcade-back-link" onClick={() => history.goBack()}>&#8592; Salir</span>
            </div>

            {/* Pause button */}
            {gamePhase === 'playing' && (
                <button className="arcade-pause-btn" onClick={togglePause}>
                    {isPaused ? '▶' : '⏸'}
                </button>
            )}

            {/* Three.js canvas */}
            <div ref={containerRef} className="arcade-canvas-container" />

            {/* HUD */}
            {gamePhase === 'playing' && (
                <div className="arcade-hud">
                    <div className="arcade-hud-score">
                        <span className="score-player">{score.player}</span>
                        <div className="score-center">
                            <span className="score-half">{half === 1 ? '1st Half' : '2nd Half'}</span>
                            <span className="score-timer">{formatTime(timeLeft)}</span>
                            {activePlayerInfo && (
                                <span className="active-player-inline">
                                    #{activePlayerInfo.number} {activePlayerInfo.name}
                                </span>
                            )}
                        </div>
                        <span className="score-ai">{score.ai}</span>
                    </div>

                    {inShootZone && (
                        <div className={`arcade-shoot-btn${half === 2 ? ' arcade-shoot-btn--top' : ''}`}>
                            SHOOT!
                        </div>
                    )}
                </div>
            )}

            {/* Commentary flash */}
            {commentary && (
                <div key={commentaryKey} className="arcade-commentary">
                    {commentary}
                </div>
            )}

            {/* Scorer popup */}
            {scorer && (
                <div key={scorerKey} className="arcade-scorer">
                    <span className="scorer-number">#{scorer.number}</span>
                    <span className="scorer-name">{scorer.name}</span>
                </div>
            )}

            {/* Faceoff countdown */}
            {countdown && gamePhase === 'playing' && (
                <div key={countdown} className={`arcade-countdown${countdown === 'GO!' ? ' arcade-countdown--go' : ''}`}>
                    {countdown}
                </div>
            )}

            {/* Pause overlay */}
            {isPaused && gamePhase === 'playing' && (
                <div className="arcade-pause-overlay">
                    <h2 className="pause-title">PAUSA</h2>
                    <div className="pause-rosters">
                        <div className="pause-team">
                            <div className="pause-team-label pause-team-label--player">Tu equipo</div>
                            {(playerRoster ?? []).map((p, i) => (
                                <div key={i} className="pause-player-row">
                                    <span className="pause-player-number">#{p.number}</span>
                                    <span className="pause-player-name">{p.name}</span>
                                    {i === 0 && <span className="pause-player-pos">POR</span>}
                                </div>
                            ))}
                        </div>
                        <div className="pause-divider" />
                        <div className="pause-team">
                            <div className="pause-team-label pause-team-label--ai">Rival</div>
                            {(aiRoster ?? []).map((p, i) => (
                                <div key={i} className="pause-player-row">
                                    <span className="pause-player-number">#{p.number}</span>
                                    <span className="pause-player-name">{p.name}</span>
                                    {i === 0 && <span className="pause-player-pos">POR</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                    <button className="pause-resume-btn" onClick={togglePause}>▶ Reanudar</button>
                </div>
            )}

            {/* Half-time screen */}
            {gamePhase === 'halftime' && (
                <HalfTimeScreen score={score} onStart={startSecondHalf} />
            )}

            {/* Game over screen */}
            {gamePhase === 'gameover' && (
                <GameOverScreen score={score} onPlayAgain={playAgain} />
            )}
        </div>
    );
}
